<template>
    <v-card class="scrollable-container pb-2 elevation-5">
        <v-card-title>
            <data-search-bar
                :title="title"
                :help-href="helpUrl"
                :options="dataSearchBarOptions"
                show-filters
                :is-filters-active="isFiltersActive"
                @change="onDataSearchBarChange"
                @filter="onFilterClick"
                @refresh="onRefreshClick"
            >
                <template v-slot:below-title>
                    <v-col>
                        <filter-chips
                            :items="filterChips"
                            @change="onFilterChipsChange"
                        ></filter-chips>
                    </v-col>
                </template>
            </data-search-bar>
        </v-card-title>
        <v-card-text class="scrollable-content pb-0">
            <data-table-view
                v-model="selectedItems"
                :headers="tableFields"
                :items="users"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                :page="page"
                :items-per-page="itemsPerPage"
                :loading="isDataLoading"
                :zebra-stripping="zebraStripping"
                :show-select="true"
                :toolbar-actions="toolbarActions"
                toolbar-class="mx-4"
                allow-export
                @update-sort-by="onUpdateSortBy"
                @export="onExportDataClick"
                @toolbar="onToolbarClick"
            >
                <!-- ============== Table actions template ============== -->
                <template v-slot:tableButtons="{ item }">
                    <span
                        v-show="
                            !item.xocAuth &&
                                $can('edit', 'User') &&
                                $can('show', 'Buttons/users/sendPasswordEmail')
                        "
                        class="mr-8"
                    >
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-icon
                                    color="accent"
                                    :disabled="item.xocAuth"
                                    cy-data="email-row"
                                    @click="onSendPasswordEmailClick(item)"
                                    v-on="on"
                                    >{{ icons.email }}
                                </v-icon>
                            </template>
                            <span class="text-caption"
                                >Send password email</span
                            >
                        </v-tooltip>
                    </span>
                    <span
                        v-show="
                            $can('read', 'User') &&
                                $can('show', 'Buttons/users/viewUser')
                        "
                    >
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-icon
                                    color="accent"
                                    cy-data="view-row"
                                    @click="onEditUserClick(item, $event)"
                                    v-on="on"
                                    >{{ icons.viewItem }}
                                </v-icon>
                            </template>
                            <span class="text-caption">View user details</span>
                        </v-tooltip>
                    </span>
                </template>
            </data-table-view>
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-show="
                            $can('create', 'User') &&
                                $can('show', 'Buttons/users/addUser')
                        "
                        fab
                        fixed
                        right
                        bottom
                        small
                        color="primary"
                        class="mb-5 mr-1"
                        aria-label="Add new user"
                        cy-data="add-row"
                        v-on="on"
                        @click="onAddUserClick"
                    >
                        <v-icon>{{ icons.add }}</v-icon>
                    </v-btn>
                </template>
                <span>Add new user</span>
            </v-tooltip>
            <component
                :is="userDetailsDialogComponent"
                :show-dialog="showEditUserDialog"
                :user-id="selectedUserId"
                :profile-type="newUserProfileType"
                @close="onUserDialogClose"
            />
            <component
                :is="exportDialogComponent"
                title="Export to CSV: Spaces"
                :entity-name="exportEntityName"
                :data-source-params="exportParams"
                :output-fields="exportFields"
                :output-file-name="exportFileName"
                @close="onExportDialogClose"
            ></component>
            <profile-select
                :show-dialog="showProfileSelectDialog"
                @close="onProfileSelectClose"
            ></profile-select>
            <confirmation-dialog ref="confirmationDialog"></confirmation-dialog>
        </v-card-text>
        <user-filters
            v-model="showFilters"
            title="User filters"
            :allow-xoc-filter="isXocUser"
            :filters="filters"
            @apply="onApplyFilters"
        ></user-filters>
    </v-card>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import CsvDataExporter from '@/components/Export/CsvDataExporter';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import DataSearchBar from '@/components/Data/DataSearchBar';
import DataTableView from '@/components/Data/DataTableView';
import FilterChips from '@/components/Filters/FilterChips';
import UserDetailsDialog from './components/UserDetailsDialog';
import UserFilters from './filters/UserFilters';
import { isEmpty, isEmptyValues } from '@/common/helpers';
import ProfileSelect from './components/ProfileSelect';

export default {
    name: 'UsersPage',
    components: {
        'data-search-bar': DataSearchBar,
        'data-table-view': DataTableView,
        'user-details-dialog': UserDetailsDialog,
        'csv-data-exporter': CsvDataExporter,
        'confirmation-dialog': ConfirmationDialog,
        'user-filters': UserFilters,
        'filter-chips': FilterChips,
        'profile-select': ProfileSelect
    },
    data() {
        return {
            title: 'Users',
            isDataLoading: false,
            userDetailsDialogComponent: '',
            showEditUserDialog: false,
            selectedUserId: 0,
            exportDialogComponent: '',
            exportEntityName: 'users',
            exportParams: {},
            lastFocusedElement: null,
            selectedItems: [],
            filterChips: [],
            showFilters: false,
            showProfileSelectDialog: false,
            newUserProfileType: ''
        };
    },
    computed: {
        ...mapState('users', [
            'tableFields',
            'computedFields',
            'filterFields',
            'exportFields',
            'exportFileName',
            'requestQuery',
            'responseQuery',
            'filterQuery',
            'zebraStripping',
            'helpUrl'
        ]),
        ...mapState('users', {
            users: 'items'
        }),
        ...mapGetters('authUser', ['isXocUser']),
        ...mapState('venue', ['selectedVenue']),
        ...mapState('brand', ['mso']),
        dataSearchBarOptions() {
            return {
                page: this.requestQuery.number,
                itemsPerPage: this.requestQuery.size,
                pageCount: this.responseQuery.totalPages,
                itemsLength: this.responseQuery.totalElements,
                query: this.requestQuery.q
            };
        },
        sortBy() {
            return this.requestQuery.sort;
        },
        sortDesc() {
            return this.requestQuery.desc;
        },
        page() {
            return this.requestQuery.number;
        },
        itemsPerPage() {
            return this.requestQuery.size;
        },
        filters() {
            return this.filterQuery;
        },
        isFiltersActive() {
            return !isEmptyValues(this.filterQuery);
        },
        toolbarActions() {
            let actions = [];
            if (this.$can('show', 'Buttons/users/deleteUser')) {
                actions.push({
                    name: 'delete',
                    text: 'Delete',
                    icon: 'delete',
                    tooltip: 'Delete users'
                });
            }
            return actions;
        }
    },
    async created() {
        if (this.tableFields && !this.tableFields.length) {
            await this.setupView();
            await this.resetRequestQuery();
        }
    },
    async mounted() {
        this.isDataLoading = true;
        setTimeout(() => {
            this.findUsers(this.getInitialPageParams(), this.filterQuery);
        }, 200);
    },
    methods: {
        // =================== Vuex Actions =====================
        ...mapActions('users', [
            'fetchUsers',
            'setupView',
            'resetRequestQuery',
            'setVenueFilters',
            'postBulkActions'
        ]),
        ...mapActions('user', ['sendResetPasswordEmail']),
        // ===================== Events ========================
        onEditUserClick(item, event) {
            this.userDetailsDialogComponent = 'user-details-dialog';
            this.showEditUserDialog = true;
            this.selectedUserId = item.id;
            this.lastFocusedElement = event.target;
        },
        onAddUserClick(event) {
            this.userDetailsDialogComponent = 'user-details-dialog';
            this.selectedUserId = 0;
            this.showEditUserDialog = true;
            this.lastFocusedElement = event.target;
            // this.showProfileSelectDialog = true;
        },
        onUserDialogClose(refreshData) {
            // if refreshData then we need to refresh the current data.
            this.userDetailsDialogComponent = '';
            this.showEditUserDialog = false;
            this.selectedUserId = 0;
            if (refreshData) {
                this.findUsers(this.getInitialPageParams(), this.filterQuery);
            }
            this.setNextFocus(this.lastFocusedElement);
        },
        onDataSearchBarChange(options) {
            this.findUsers(
                {
                    ...options,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filterQuery
            );
        },
        onUpdateSortBy(sortOptions) {
            if (sortOptions) {
                this.findUsers(
                    {
                        ...this.dataSearchBarOptions,
                        ...sortOptions
                    },
                    this.filterQuery
                );
            }
        },
        async onSendPasswordEmailClick(item) {
            await this.sendResetPasswordEmail({
                userId: item.id,
                url: `${window.location.origin}/change-password`,
                mso: this.mso
            });
        },
        onExportDataClick(event) {
            const searchParams = {
                number: 1,
                size:
                    this.dataSearchBarOptions.itemsLength <= 10000
                        ? this.dataSearchBarOptions.itemsLength
                        : 10000,
                q: this.dataSearchBarOptions.query,
                sort: this.sortBy,
                desc: this.sortDesc
            };
            const filterParams = this.filterQuery;
            this.exportParams = {
                searchParams,
                filterParams,
                isDsSearch: false
            };
            this.exportDialogComponent = 'csv-data-exporter';
            this.lastFocusedElement = event.target;
        },
        onExportDialogClose() {
            this.exportDialogComponent = '';
            this.setNextFocus(this.lastFocusedElement);
        },
        async onToolbarClick({ item, selected }) {
            const action = item.name;
            const criteria = { ids: selected.map(item => item.id) };
            let needsRefresh = false;
            if (item.name === 'delete') {
                const res = await this.$refs.confirmationDialog.open(
                    'Delete Users',
                    'Are you sure you want to delete the selected users?',
                    '',
                    { showIcon: true }
                );
                if (res) {
                    await this.postBulkActions({ action, criteria });
                    needsRefresh = true;
                }
            }
            if (needsRefresh) {
                this.refreshView();
                this.selectedItems = [];
            }
        },
        onFilterClick() {
            this.showFilters = true;
        },
        onApplyFilters({ filters, filtersDesc }) {
            this.applyFilters(filters, filtersDesc);
        },
        onFilterChipsChange(filtersDesc) {
            if (filtersDesc) {
                let newFilters = {};
                filtersDesc.forEach(item => {
                    newFilters[item.name] = item.value;
                });
                this.applyFilters(newFilters, filtersDesc);
            }
        },
        onProfileSelectClose(value) {
            this.showProfileSelectDialog = false;
            if (value) {
                this.$nextTick(() => {
                    this.userDetailsDialogComponent = 'user-details-dialog';
                    this.selectedUserId = 0;
                    this.newUserProfileType = value || '';
                    this.showEditUserDialog = true;
                });
            }
        },
        onRefreshClick() {
            this.refreshView();
        },
        // ===================== Others ========================
        refreshView() {
            this.findUsers(
                {
                    ...this.dataSearchBarOptions,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filterQuery
            );
        },
        applyFilters(filters, filtersDesc) {
            let altFilters = {
                ...filters
            };
            let altFiltersDesc = [...filtersDesc];
            if (filters.currentVenueOnly) {
                altFilters.mso = this.selectedVenue.mso.identifier;
                altFilters.partner = this.selectedVenue.partner.identifier;
                altFilters.venue = this.selectedVenue.venue.identifier;
            } else {
                delete altFilters.mso;
                delete altFilters.partner;
                delete altFilters.venue;
                delete altFilters.currentVenueOnly;
                altFiltersDesc = altFiltersDesc.filter(
                    item => item.name !== 'currentVenueOnly'
                );
            }
            this.filterChips = altFiltersDesc;

            this.findUsers(
                {
                    ...this.dataSearchBarOptions,
                    page: 1,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                altFilters
            );
        },
        async findUsers(searchOptions, filterOptions) {
            this.isDataLoading = true;
            try {
                if (!isEmpty(searchOptions)) {
                    const searchParams = {
                        number: searchOptions.page,
                        size: searchOptions.itemsPerPage,
                        q: searchOptions.query,
                        sort: searchOptions.sortBy,
                        desc: searchOptions.sortDesc
                    };
                    const filterParams = { ...filterOptions };
                    await this.fetchUsers({ searchParams, filterParams });
                }
            } finally {
                this.isDataLoading = false;
            }
        },
        getInitialPageParams() {
            return {
                page: 1,
                itemsPerPage: this.itemsPerPage,
                query: this.requestQuery.q,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            };
        },
        setNextFocus(sourceElement) {
            const keydownEsc = new KeyboardEvent('keydown', { keyCode: 27 });
            this.$nextTick(() => {
                if (sourceElement) {
                    const button = sourceElement.closest('button');
                    button.focus();
                    button.dispatchEvent(keydownEsc);
                }
            });
        }
    }
};
</script>
